import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, X, Smartphone, Share } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// iOS Safari Share Icon Component
const IOSShareIcon = () => (
    <svg 
        className="w-5 h-5 inline-block mx-0.5 -mt-0.5" 
        viewBox="0 0 50 50" 
        fill="currentColor"
    >
        <path d="M30.3 13.7L25 8.4l-5.3 5.3-1.4-1.4L25 5.6l6.7 6.7z"/>
        <path d="M24 7h2v21h-2z"/>
        <path d="M35 40H15c-1.7 0-3-1.3-3-3V19c0-1.7 1.3-3 3-3h7v2h-7c-.6 0-1 .4-1 1v18c0 .6.4 1 1 1h20c.6 0 1-.4 1-1V19c0-.6-.4-1-1-1h-7v-2h7c1.7 0 3 1.3 3 3v18c0 1.7-1.3 3-3 3z"/>
    </svg>
);

export const InstallPrompt: React.FC = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [isSafari, setIsSafari] = useState(false);

    useEffect(() => {
        // Check if already installed (standalone mode)
        const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
            || (window.navigator as any).standalone === true;
        setIsStandalone(isInStandaloneMode);

        // Check if iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        setIsIOS(isIOSDevice);

        // Check if Safari on iOS (not Chrome or other browsers on iOS)
        const isSafariBrowser = isIOSDevice && 
            /Safari/.test(navigator.userAgent) && 
            !/CriOS/.test(navigator.userAgent) && // Not Chrome
            !/FxiOS/.test(navigator.userAgent) && // Not Firefox
            !/EdgiOS/.test(navigator.userAgent);  // Not Edge
        setIsSafari(isSafariBrowser);

        // Listen for beforeinstallprompt event (Android/Chrome)
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);

            // Check if user has dismissed before (stored in localStorage)
            const dismissed = localStorage.getItem('pwa-install-dismissed');
            const dismissedTime = dismissed ? parseInt(dismissed) : 0;
            const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

            // Show again after 7 days
            if (!dismissed || daysSinceDismissed > 7) {
                setShowPrompt(true);
            }
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Show iOS prompt if not installed and on iOS Safari
        // Only show on Safari since other iOS browsers don't support PWA install
        if (isIOSDevice && !isInStandaloneMode) {
            const dismissed = localStorage.getItem('pwa-install-dismissed-ios');
            const dismissedTime = dismissed ? parseInt(dismissed) : 0;
            const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

            // Show after a small delay to not be too aggressive
            if (!dismissed || daysSinceDismissed > 7) {
                setTimeout(() => {
                    setShowPrompt(true);
                }, 3000); // 3 second delay for iOS
            }
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;

        try {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === 'accepted') {
                console.log('[PWA] User accepted install prompt');
            } else {
                console.log('[PWA] User dismissed install prompt');
                localStorage.setItem('pwa-install-dismissed', Date.now().toString());
            }
        } catch (error) {
            console.error('[PWA] Install error:', error);
        }

        setDeferredPrompt(null);
        setShowPrompt(false);
    };

    const handleDismiss = () => {
        if (isIOS) {
            localStorage.setItem('pwa-install-dismissed-ios', Date.now().toString());
        } else {
            localStorage.setItem('pwa-install-dismissed', Date.now().toString());
        }
        setShowPrompt(false);
    };

    // Don't render if already installed or shouldn't show
    if (isStandalone || !showPrompt) {
        return null;
    }

    return (
        <div className="fixed bottom-4 left-4 right-4 z-[9999] md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4 duration-300">
            <Card className="shadow-2xl border-0 bg-gradient-to-r from-indigo-500 to-purple-600 text-white overflow-hidden">
                <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                            <Smartphone className="w-6 h-6" />
                        </div>

                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base mb-1">Install Ripplefy</h3>

                            {isIOS ? (
                                <div className="text-sm text-white/90 mb-3 space-y-1.5">
                                    <p className="font-medium">To install this app:</p>
                                    <ol className="list-decimal list-inside space-y-1 text-xs">
                                        <li>Tap the Share button <IOSShareIcon /> at the bottom of Safari</li>
                                        <li>Scroll down and tap <span className="font-medium">"Add to Home Screen"</span></li>
                                        <li>Tap <span className="font-medium">"Add"</span> to confirm</li>
                                    </ol>
                                </div>
                            ) : (
                                <p className="text-sm text-white/90 mb-3">
                                    Install for quick access, offline support & a native app experience
                                </p>
                            )}

                            <div className="flex items-center gap-2">
                                {!isIOS && (
                                    <Button
                                        onClick={handleInstall}
                                        size="sm"
                                        className="bg-white text-indigo-600 hover:bg-white/90 font-medium"
                                    >
                                        <Download className="w-4 h-4 mr-1.5" />
                                        Install
                                    </Button>
                                )}
                                <Button
                                    onClick={handleDismiss}
                                    size="sm"
                                    variant="ghost"
                                    className="text-white hover:bg-white/20"
                                >
                                    {isIOS ? 'Got it' : 'Not now'}
                                </Button>
                            </div>
                        </div>

                        <button
                            onClick={handleDismiss}
                            className="flex-shrink-0 p-1 hover:bg-white/20 rounded-full transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default InstallPrompt;
